/**
 * Quand quitter la micro-entreprise : le chiffre d'affaires à partir duquel un
 * autre régime vous laisse assez de plus pour que ça vaille le changement.
 *
 * LA QUESTION N'EST PAS « LEQUEL AUJOURD'HUI », C'EST « À PARTIR DE QUAND ».
 * Le comparateur de régimes répond pour un chiffre d'affaires donné. Le
 * solopreneur qui grandit veut savoir jusqu'où il peut rester où il est. On
 * garde donc la part de ses dépenses constante, c'est l'hypothèse la plus
 * simple pour une activité qui croît, et l'on fait monter le chiffre
 * d'affaires jusqu'au point où quitter la micro rapporte plus de mille euros
 * par an.
 *
 * MILLE EUROS, COMME PARTOUT DANS LA RUBRIQUE. En dessous, l'écart est plus
 * petit que ce que coûte la comptabilité d'un régime réel ou d'une société,
 * qu'aucune source officielle ne chiffre, et que l'imprécision du calcul.
 *
 * CE QU'ON TROUVE : CE N'EST PAS LE CHIFFRE D'AFFAIRES QUI DÉCIDE, C'EST LA
 * PART DES DÉPENSES. En libéral avec un dixième de dépenses, la micro reste
 * devant jusqu'à son plafond ; avec un cinquième, la quitter rapporte dès
 * 28 000 €. L'écran montre donc d'abord le montant de dépenses au-delà duquel
 * changer rapporte, à votre chiffre d'affaires, et la courbe de ce seuil
 * quand le chiffre d'affaires monte.
 *
 * LES CALCULS SONT CEUX DU COMPARATEUR (businessStructure.js) : mêmes barèmes,
 * mêmes hypothèses, en particulier une société qui verse tout en rémunération.
 * Les deux outils ne peuvent donc pas se contredire.
 */

import { ECART_NEGLIGEABLE, bascule, comparerRegimes } from "./businessStructure.js";
import { PLAFONDS, SEUILS_TVA } from "./microEnterprise.js";

/** Le pas de recherche du chiffre d'affaires de bascule. */
export const PAS_RECHERCHE = 1000;

/** Le nombre de points de la courbe. */
export const POINTS_COURBE = 40;

/**
 * À un chiffre d'affaires donné, les dépenses au-delà desquelles quitter la
 * micro rapporte au moins mille euros par an. Dichotomie : plus on dépense,
 * plus la micro, qui ne déduit rien, perd du terrain. Nul si même en
 * dépensant tout le chiffre d'affaires le gain n'y est pas.
 */
export function seuilDepenses({ chiffreAffaires, ...reste }) {
    const gagne = (charges) =>
        comparerRegimes({ chiffreAffaires, charges, ...reste }).gainSiChangement >= ECART_NEGLIGEABLE;
    if (!(chiffreAffaires > 0) || !gagne(chiffreAffaires)) return null;
    if (gagne(0)) return 0;
    let bas = 0;
    let haut = chiffreAffaires;
    for (let i = 0; i < 30; i++) {
        const milieu = (bas + haut) / 2;
        if (gagne(milieu)) haut = milieu;
        else bas = milieu;
    }
    // Arrondi vers le haut : à l'euro près du dessous, le gain retombait sous
    // les mille euros (999,97 € à 40 000 € en libéral, vu le 25/09/2026).
    return Math.ceil(haut);
}

/** Le seuil de TVA de base qui s'applique à une activité de la micro. */
export function seuilTvaDe(activite) {
    return SEUILS_TVA[activite === "vente" ? "global" : "services"].base;
}

export function quandQuitterLaMicro({
    chiffreAffaires,
    charges = 0,
    activite = "services",
    artisan = false,
    enCouple = false,
    enfants = 0,
    parentIsole = false,
    autresRevenus = 0,
}) {
    const foyer = { enCouple, enfants, parentIsole, autresRevenus };
    const commun = { activite, artisan, ...foyer };
    const part = chiffreAffaires > 0 ? Math.min(1, Math.max(0, charges / chiffreAffaires)) : 0;
    const plafond = PLAFONDS[activite];

    const aujourdhui = comparerRegimes({ chiffreAffaires, charges, ...commun });
    const a = (ca) => comparerRegimes({ chiffreAffaires: ca, charges: part * ca, ...commun });

    // Le premier chiffre d'affaires, de mille en mille jusqu'au plafond, où
    // quitter la micro rapporte au moins mille euros par an.
    let seuil = null;
    let regimeAuSeuil = null;
    for (let ca = PAS_RECHERCHE; ca <= plafond; ca += PAS_RECHERCHE) {
        const r = a(ca);
        if (r.gainSiChangement >= ECART_NEGLIGEABLE) {
            seuil = ca;
            regimeAuSeuil = r.meilleurRegime;
            break;
        }
    }

    // La courbe : pour chaque chiffre d'affaires jusqu'au plafond, la part de
    // dépenses au-delà de laquelle quitter la micro rapporte mille euros.
    const pas = Math.max(PAS_RECHERCHE, Math.round(plafond / POINTS_COURBE / PAS_RECHERCHE) * PAS_RECHERCHE);
    const courbe = [];
    for (let ca = pas; ca <= plafond; ca += pas) {
        const depenses = seuilDepenses({ chiffreAffaires: ca, ...commun });
        courbe.push({ chiffreAffaires: ca, part: depenses === null ? null : depenses / ca });
    }

    return {
        part,
        aujourdhui,
        seuil,
        regimeAuSeuil,
        // Déjà passé : le lecteur gagnerait dès aujourd'hui à changer.
        dejaPasse: aujourdhui.changementUtile,
        // À chiffre d'affaires constant : les dépenses au-delà desquelles
        // quitter la micro rapporte mille euros, et celles où le réel passe
        // tout juste devant (le calcul du comparateur).
        depensesUtiles: seuilDepenses({ chiffreAffaires, ...commun }),
        basculeDepenses: bascule({ chiffreAffaires, activite, artisan, ...foyer }),
        plafond,
        seuilTva: seuilTvaDe(activite),
        courbe,
    };
}
